import React, { useContext, useState } from "react";
import { Modal, ModalBody } from "reactstrap";
import { Button } from "react-bootstrap";
import { FiX } from "react-icons/fi";
import toast from "react-hot-toast";
import moment from "moment/moment";
import { cancelSubscription } from "../../../api/context/billing";
import { SelectionContext } from "../../../Context/Store";

const CancelSubscriptionModal = ({
  isOpen,
  toggle,
  subscription,
  scheduledNextPlan,
  mode = "current",
}) => {
  const { refreshBillingBalance } = useContext(SelectionContext);
  const [loading, setLoading] = useState(false);

  const planName = subscription?.planName || "current";
  const quota = subscription?.quota ?? 0;
  const periodEnd = subscription?.currentPeriodEnd
    ? moment(subscription.currentPeriodEnd).format("MMM D, YYYY")
    : null;

  const upgradePlanName = scheduledNextPlan?.planName || "upcoming plan";
  const upgradeStart = scheduledNextPlan?.scheduledStartAt
    ? moment(scheduledNextPlan.scheduledStartAt).format("MMM D, YYYY")
    : null;

  const isUpgradeMode = mode === "upgrade";

  const handleCancel = async () => {
    setLoading(true);
    try {
      await cancelSubscription({ cancelAtPeriodEnd: true });
      toast.success(
        isUpgradeMode
          ? `We've cancelled the upcoming switch to ${upgradePlanName}.`
          : periodEnd
          ? `Subscription cancelled. You still have access until ${periodEnd}`
          : "Subscription cancelled"
      );
      refreshBillingBalance();
      toggle();
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Could not cancel subscription"
      );
    } finally {
      setLoading(false);
    }
  };

  const title = isUpgradeMode
    ? `Cancel the switch to ${upgradePlanName}?`
    : "Are you sure you want to cancel?";

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      className="billing-cancel-modal"
    >
      <ModalBody>
        <div className="billing-cancel-header">
          <h4 className="billing-cancel-title">{title}</h4>
          <button
            type="button"
            className="billing-cancel-close"
            onClick={toggle}
            disabled={loading}
            aria-label="Close"
          >
            <FiX />
          </button>
        </div>

        {isUpgradeMode ? (
          <>
            <p className="billing-cancel-body">
              Your <strong>{planName}</strong> plan will keep running
              {periodEnd && <> until <strong>{periodEnd}</strong></>}, and{" "}
              <strong>{upgradePlanName}</strong>
              {upgradeStart && <> (scheduled for {upgradeStart})</>} won't
              activate.
            </p>
            <ul className="billing-cancel-notes">
              <li>
                You'll stay on {planName} at your current rate — no charge for
                the upcoming plan.
              </li>
              <li>
                You can schedule a different plan any time before your current
                one ends.
              </li>
            </ul>
          </>
        ) : (
          <>
            <p className="billing-cancel-body">
              Your <strong>{planName}</strong> plan stays active until{" "}
              <strong>{periodEnd || "the end of your billing period"}</strong>.
              {quota > 0 && (
                <> You'll keep all {quota} monthly credits until then.</>
              )}{" "}
              After that it won't renew.
            </p>
            <ul className="billing-cancel-notes">
              <li>
                Top-up credits you've already bought never expire —
                cancelling doesn't touch them.
              </li>
              <li>
                This can't be undone. Coming back means subscribing again and
                approving a new payment mandate.
              </li>
            </ul>
          </>
        )}

        <div className="billing-cancel-actions">
          <Button
            className="billing-cancel-keep-btn"
            onClick={toggle}
            disabled={loading}
          >
            {isUpgradeMode ? `Keep ${upgradePlanName} queued` : "Keep my plan"}
          </Button>
          <Button
            className="billing-cancel-confirm-btn"
            onClick={handleCancel}
            disabled={loading}
          >
            {loading
              ? "Cancelling..."
              : isUpgradeMode
              ? "Yes, cancel switch"
              : "Yes, cancel"}
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default CancelSubscriptionModal;
